/**
 * Sidebar navigation component.
 *
 * SPEC FE-1 / R3a: the nav body is extracted into NavContent so the SAME markup
 * (and the SAME role-gating) renders both in the desktop fixed sidebar
 * (Navigation, lg and up) and inside the mobile Sheet drawer (AppLayout renders
 * <NavContent/> in the Sheet). The role-gating conditionals
 * (is_admin/is_super_admin, the dialerOnly filter) are PRESERVED byte-for-byte -
 * no nav item becomes reachable to a role that could not reach it before.
 */

import { NavLink } from 'react-router-dom';
import { Phone, Users, Calendar, BarChart3, LogOut, Workflow, Flame, Mic, Bot, Link2, KanbanSquare, MessageSquare, Building2, GitBranch, CalendarDays, FileSignature } from 'lucide-react';
import { useAuth, useHasDialer } from '@/lib/auth/AuthProvider';
import { auth } from '@/lib/firebase';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { TenantSwitcher } from '@/components/TenantSwitcher';
import { BRAND_NAME, BRAND_LOGO } from '@/lib/brand';

const navItems = [
  { to: '/dashboard', icon: Phone, label: 'Dialer', dialerOnly: true },
  { to: '/inbound', icon: Calendar, label: 'Inbound' },
  { to: '/warm-leads', icon: Flame, label: 'Warm Leads' },
  { to: '/cold-calling', icon: Users, label: 'Cold Dialing', dialerOnly: true },
  { to: '/pipeline', icon: KanbanSquare, label: 'Pipeline' },
  { to: '/ai-drafts', icon: Bot, label: 'AI Drafts' },
  { to: '/inbox', icon: MessageSquare, label: 'Inbox' },
  { to: '/sequences', icon: Workflow, label: 'Sequences', dialerOnly: true },
  { to: '/social-planner', icon: CalendarDays, label: 'Social Planner' },
  { to: '/workflows', icon: GitBranch, label: 'Workflows', dialerOnly: true },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
    isActive
      ? 'bg-primary text-primary-foreground'
      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
  );

/**
 * The shared nav body (header + links + user section). Rendered inside the
 * desktop sidebar AND inside the mobile Sheet drawer.
 *
 * @param onNavigate optional callback fired when a nav link is clicked - the
 *   mobile drawer passes a setOpen(false) so the drawer closes on navigation.
 */
export function NavContent({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { user } = useAuth();
  const hasDialer = useHasDialer();

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Per-tenant active-tenant display (correct as-is); only the literal brand
  // FALLBACK is now the build-time product brand so an authless/no-tenant render
  // shows "Metabus" on the metabus build instead of "BuildGravity".
  const tenantName = user?.tenant?.name || user?.tenant_id || BRAND_NAME;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center gap-2">
          {/* RULE 0: BRAND_LOGO defaults to "" so the buildgravity build renders
              text-only exactly as before; the metabus build supplies a path and
              shows the mark next to the tenant name. */}
          {BRAND_LOGO && (
            <img
              src={BRAND_LOGO}
              alt={BRAND_NAME}
              className="h-7 w-auto shrink-0"
            />
          )}
          <h1 className="text-xl font-heading font-bold tracking-tight">{tenantName}</h1>
        </div>
        <p className="text-sm text-muted-foreground">{hasDialer ? 'Sales Engine' : 'Sales CRM'}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.filter((item) => !item.dialerOnly || hasDialer).map((item) => (
          <NavLink key={item.to} to={item.to} className={navLinkClass} onClick={onNavigate}>
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
        {(user?.is_admin || user?.is_super_admin) && (
          <NavLink to="/integrations" className={navLinkClass} onClick={onNavigate}>
            <Link2 className="h-5 w-5" />
            Integrations
          </NavLink>
        )}
        {user?.is_super_admin && (
          <NavLink to="/agency" className={navLinkClass} onClick={onNavigate}>
            <Building2 className="h-5 w-5" />
            Agency
          </NavLink>
        )}
        {user?.is_super_admin && (
          <NavLink to="/voice-agent" className={navLinkClass} onClick={onNavigate}>
            <Mic className="h-5 w-5" />
            Voice Agent
          </NavLink>
        )}
        {user?.is_super_admin && (
          <NavLink to="/esign" className={navLinkClass} onClick={onNavigate}>
            <FileSignature className="h-5 w-5" />
            E-Sign Requests
          </NavLink>
        )}
      </nav>

      {/* User section */}
      <div className="p-4 border-t space-y-2">
        <div className="px-3 py-2">
          <p className="text-sm font-medium truncate">{user?.name || user?.email}</p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
        <TenantSwitcher />
        <Button
          variant="ghost"
          className="w-full justify-start"
          onClick={handleSignOut}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}

/**
 * Desktop sidebar (lg and up). Hidden below lg, where AppLayout renders the
 * hamburger + Sheet drawer instead.
 */
export function Navigation() {
  return (
    <aside className="hidden lg:flex w-64 border-r bg-card flex-col">
      <NavContent />
    </aside>
  );
}
